import React, { useState } from 'react';
import { 
    Box, 
    Container, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    TextField, 
    Button, 
    Paper,
    Divider 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';

// Styled components
const StyledHero = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(8, 0),
    textAlign: 'center',
    color: 'white',
}));

const StyledSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0),
}));

const ContactCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    color: 'white',
}));

const ContactForm = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

export default function ContactUs() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Handle form submission here
        console.log('Form submitted:', formData);
        // You can add your form submission logic here
    };

    return (
        <Box>
            {/* Hero Section */}
            <StyledHero>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h2" 
                        component="h1" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 'bold',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                        }}
                    >
                        Contact Us
                    </Typography>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            maxWidth: '800px', 
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontWeight: 300,
                            opacity: 0.95
                        }}
                    >
                        At Glamour & Radiance, your satisfaction is our priority. Whether you have questions about our products, need assistance with an order, or want to collaborate with us, we're here to help.
                    </Typography>
                </Container>
            </StyledHero>

            {/* Description Section */}
            <StyledSection>
                <Container maxWidth="md">
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            textAlign: 'center',
                            lineHeight: 1.8,
                            color: 'text.secondary',
                            fontWeight: 400,
                            fontSize: '1.2rem'
                        }}
                    >
                        Our team is dedicated to providing prompt and friendly support to ensure your experience with us is as radiant as our skincare solutions.
                    </Typography>
                </Container>
            </StyledSection>

            {/* Contact Information Cards */}
            <StyledSection sx={{ backgroundColor: 'grey.50' }}>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 6
                        }}
                    >
                        Get In Touch
                    </Typography>
                    
                    <Grid container spacing={4}>
                        {/* Address Card */}
                        <Grid item xs={12} md={4}>
                            <ContactCard>
                                <IconWrapper>
                                    <LocationOnIcon fontSize="large" />
                                </IconWrapper>
                                <Typography 
                                    variant="h6" 
                                    component="h3" 
                                    gutterBottom 
                                    sx={{ fontWeight: 600, color: 'primary.main' }}
                                >
                                    Address
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        lineHeight: 1.6,
                                        color: 'text.secondary'
                                    }}
                                >
                                    Glamour and Radiance India:<br />
                                    456/Industrial Area Phase 1,<br />
                                    Panchkula, Haryana 134113
                                </Typography>
                            </ContactCard>
                        </Grid>

                        {/* Email Card */}
                        <Grid item xs={12} md={4}>
                            <ContactCard>
                                <IconWrapper>
                                    <EmailIcon fontSize="large" />
                                </IconWrapper>
                                <Typography 
                                    variant="h6" 
                                    component="h3" 
                                    gutterBottom 
                                    sx={{ fontWeight: 600, color: 'primary.main' }}
                                >
                                    Email
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        lineHeight: 1.6,
                                        color: 'text.secondary'
                                    }}
                                >
                                    <Box 
                                        component="a" 
                                        href="mailto:info@glamourradiance.com"
                                        sx={{ 
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        info@glamourradiance.com
                                    </Box>
                                </Typography>
                            </ContactCard>
                        </Grid>

                        {/* Phone Card */}
                        <Grid item xs={12} md={4}>
                            <ContactCard>
                                <IconWrapper>
                                    <PhoneIcon fontSize="large" />
                                </IconWrapper>
                                <Typography 
                                    variant="h6" 
                                    component="h3" 
                                    gutterBottom 
                                    sx={{ fontWeight: 600, color: 'primary.main' }}
                                >
                                    Phone
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        lineHeight: 1.6,
                                        color: 'text.secondary'
                                    }}
                                >
                                    <Box 
                                        component="a" 
                                        href="tel:+919868265827"
                                        sx={{ 
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        +91 98682 65827
                                    </Box>
                                </Typography>
                            </ContactCard>
                        </Grid>
                    </Grid>
                </Container>
            </StyledSection>

            <Divider />

            {/* Contact Form Section */}
            <StyledSection>
                <Container maxWidth="md">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 4
                        }}
                    >
                        Send us a Message
                    </Typography>
                    
                    <ContactForm component="form" onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Your Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    multiline
                                    rows={5}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        endIcon={<SendIcon />}
                                        sx={{
                                            background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: '25px',
                                            textTransform: 'none',
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            '&:hover': {
                                                background: 'linear-gradient(45deg, #5a6fd8, #6a42a0)',
                                            }
                                        }}
                                    >
                                        Send Message
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </ContactForm>
                </Container>
            </StyledSection>

            {/* Footer CTA */}
            <StyledSection sx={{ backgroundColor: 'primary.50' }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                            variant="h5" 
                            component="h3" 
                            gutterBottom 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            We'd Love to Hear From You!
                        </Typography>
                        <Typography 
                            variant="body1" 
                            color="text.secondary" 
                            sx={{ 
                                fontSize: '1.1rem',
                                lineHeight: 1.6
                            }}
                        >
                            Whether you're curious about our skincare products, need support, or want to explore partnership opportunities, don't hesitate to reach out. Our team is ready to assist you with personalized care and attention.
                        </Typography>
                    </Box>
                </Container>
            </StyledSection>
        </Box>
    );
}